import { inject, Injectable, OnDestroy } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { ApiService } from "./api.service";
import { environment } from "../../environments/environment";
import { catchError, tap } from "rxjs/operators";
import { BehaviorSubject, EMPTY, map, Observable, of } from "rxjs";
import { CoreService } from "./core.service";

export type FetchSbMarketApiArgs = {
  eventId: string | number;
  providerId: string | number;
};

export type Sport = {
  aliases: Record<string, any>;
  id: string;
  name: string;
  parents: any[];
  type: string;
};

@Injectable({ providedIn: "root" })
export class MarketService implements OnDestroy {
  apiService = inject(ApiService);
  coreService = inject(CoreService);
  $closedMarketAlert: BehaviorSubject<string> = new BehaviorSubject<string>("");
  sports$: BehaviorSubject<Sport[]> = new BehaviorSubject<Sport[]>([]);
  private todayMarketsCache: any = null;
  private tomorrowMarketsCache: any = null;
  private todayMarketsCacheTimestamp: number | null = null;
  private tomorrowMarketsCacheTimestamp: number | null = null;
  private locale = this.coreService.getLocale();
  private cacheInvalidationInterval: any = null;

  constructor(private http: HttpClient) {
    this.scheduleCacheInvalidation();
  }

  setMarketAlert(marketId: string) {
    this.$closedMarketAlert.next(marketId);
  }

  getMarketAlert(): Observable<string> {
    return this.$closedMarketAlert.asObservable();
  }

  fetchSports(): void {
    this.http
      .get<any>(`${environment.mURL}${environment.endpoints.sports}`, {
        params: { locale: this.locale },
      })
      .pipe(
        map((response) => response?.data ?? []),
        tap((data) => this.sports$.next(Array.isArray(data) ? data : [])),
        catchError((err) => {
          console.error("fetchSports error:", err);
          this.sports$.next([]); // optional: reset on error
          return EMPTY; // completes the stream
        }),
      )
      .subscribe();
  }

  getSports() {
    return this.http.get(`${environment.mURL}${environment.endpoints.sports}`, {
      params: { locale: this.locale },
    });
  }

  getSportType(params: any) {
    return this.http.get(`${environment.mURL}${environment.endpoints.sports}/${params.id}`, {
      params: { locale: this.locale },
    });
  }

  getGroup(params: any) {
    return this.http.get(`${environment.mURL}${environment.endpoints.group}/${params.id}`, {
      params: { locale: this.locale },
    });
  }

  getEvent(params: any) {
    return this.http.get(`${environment.mURL}${environment.endpoints.event}/${params.id}`, {
      params: { locale: this.locale },
    });
  }

  getEventMarkets(params: any) {
    return this.http.get(`${environment.mURL}${environment.endpoints.eventMarket}/${params.id}`, {
      params: { locale: this.locale },
    });
  }

  getRacingMarkets(params: any) {
    return this.http.get(`${environment.mURL}${environment.endpoints.raceMarket}/${params.id}`, {
      params: { locale: this.locale },
    });
  }

  fetchMarket(params: any) {
    return this.http.get(`${environment.mURL}${environment.endpoints.marketStatus}`, {
      params: {
        ids: params.id,
        locale: this.locale,
      },
    });
  }

  requestMarket(params: any) {
    return this.http.post(`${environment.mURL}${environment.endpoints.markets}`, {
      marketIds: params.ids,
      locale: this.locale,
    });
  }

  fetchMatchOdds(params: any) {
    return this.http.get(`${environment.mURL}${environment.endpoints.marketMatchOdds}/${params.eventTypeId}`, {
      params: {
        maxResults: params?.maxResults,
        locale: this.locale,
      },
    });
  }

  fetchInPlayMarkets(params?: any) {
    return this.http.get(`${environment.mURL}${environment.endpoints.inPlayMarkets}`, {
      params: { locale: this.locale },
    });
  }

  fetchMarketStatus(params?: any) {
    return this.http.post(`${environment.mURL}${environment.endpoints.mktStatus}`, params);
  }

  fetchSnapshots(params?: any) {
    return this.http.post(`${environment.mURL}${environment.endpoints.snapshot}`, params);
  }

  fetchTodayMarkets(params?: any) {
    if (this.todayMarketsCache && this.isCacheValid(this.todayMarketsCacheTimestamp)) {
      return of(this.todayMarketsCache);
    }

    return this.http
      .get(`${environment.mURL}${environment.endpoints.todayMarkets}`, {
        params: { ...params, locale: this.locale },
      })
      .pipe(
        tap((response) => {
          this.todayMarketsCache = response;
          this.todayMarketsCacheTimestamp = Date.now(); // Update cache timestamp
        }),
      );
  }

  fetchTomorrowMarkets(params?: any) {
    if (this.tomorrowMarketsCache && this.isCacheValid(this.tomorrowMarketsCacheTimestamp)) {
      return of(this.tomorrowMarketsCache);
    }

    return this.http
      .get(`${environment.mURL}${environment.endpoints.tomorrowMarkets}`, {
        params: { ...params, locale: this.locale },
      })
      .pipe(
        tap((response) => {
          this.tomorrowMarketsCache = response;
          this.tomorrowMarketsCacheTimestamp = Date.now(); // Update cache timestamp
        }),
      );
  }

  fetchSbBmAndFancyMarkets(params?: any) {
    return this.http.get(`${environment.mURL}${environment.endpoints.sbBmFancyMarket}/${params.eventId}`, {
      params: { locale: this.locale },
    });
  }

  fetchBmMarket(params: FetchSbMarketApiArgs) {
    return this.http.get(`${environment.mURL}${environment.endpoints.sbBm}/${params.eventId}/${params.providerId}`, {
      params: { locale: this.locale },
    });
  }

  fetchFancyMarket(params: FetchSbMarketApiArgs) {
    return this.http.get(`${environment.mURL}${environment.endpoints.sbFancy}/${params.eventId}/${params.providerId}`, {
      params: { locale: this.locale },
    });
  }

  fetchSbMarkets(params: any) {
    return this.http.get(`${environment.mURL}${environment.endpoints.sbMarkets}`, {
      params: {
        ids: params.id,
      },
    });
  }

  searchItems(params?: any) {
    return this.http.get(`${environment.mURL}${environment.endpoints.search}`, {
      params: { ...params, locale: this.locale },
    });
  }

  clearTdTmrCache() {
    this.todayMarketsCache = null;
    this.tomorrowMarketsCache = null;
    this.todayMarketsCacheTimestamp = null;
    this.tomorrowMarketsCacheTimestamp = null;
  }

  private isCacheValid(timestamp: number | null): boolean {
    if (!timestamp) return false;
    const currentTime = Date.now();
    return currentTime - timestamp < environment.cacheExpirationTime;
  }

  private scheduleCacheInvalidation() {
    if (this.cacheInvalidationInterval) {
      clearInterval(this.cacheInvalidationInterval);
    }

    this.cacheInvalidationInterval = setInterval(() => {
      this.clearTdTmrCache();
    }, environment.cacheExpirationTime);
  }

  ngOnDestroy() {
    if (this.cacheInvalidationInterval) {
      clearInterval(this.cacheInvalidationInterval);
      this.cacheInvalidationInterval = null;
    }
    // Clear caches to free memory
    this.clearTdTmrCache();
  }
}
